// src/components/user/UserList.jsx
import React, { useState, useEffect } from 'react';
import UserCard from './UserCard';
import Loader from '../common/Loader';
import { FaUserFriends } from 'react-icons/fa';
import FollowButton from './FollowButton';

const UserList = ({ 
  users, 
  loading, 
  emptyMessage = "No users found", 
  onFollowChange,
  onUserSelect,
  showBio = true,
  showStats = true,
  showFollowButton = true,
  compact = false
}) => {
  const [updatedUsers, setUpdatedUsers] = useState([]);

  // Update local state when users prop changes
  useEffect(() => {
    if (users) {
      setUpdatedUsers(users);
    }
  }, [users]);

  // Handle follow status changes
  const handleFollowChange = (userId, isFollowing, status) => {
    // Update local state with new follow status
    setUpdatedUsers(prevUsers => 
      prevUsers.map(user => 
        user._id === userId 
          ? { ...user, followInfo: { isFollowing, status } } 
          : user
      )
    );
    
    // Propagate changes to parent component if needed
    if (onFollowChange) {
      onFollowChange(userId, isFollowing, status);
    }
  };

  if (loading) {
    return (
      <div className="py-8 flex justify-center">
        <Loader />
      </div>
    );
  }

  if (!updatedUsers || updatedUsers.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500">
        <div className="mb-3 flex justify-center">
          <FaUserFriends className="text-gray-300" size={36} />
        </div>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Render compact list (for dropdowns, etc.)
  if (compact) {
    return (
      <ul className="divide-y divide-gray-100">
        {updatedUsers.map(user => (
          <li 
            key={user._id}
            className={`p-3 hover:bg-gray-50 transition-colors ${onUserSelect ? 'cursor-pointer' : ''}`}
            onClick={() => onUserSelect && onUserSelect(user)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200">
                  <img 
                    src={`${process.env.REACT_APP_ASSETS_URL}/uploads/profile/${user.avatar || 'default-avatar.png'}`} 
                    alt={user.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="ml-2 font-medium text-sm text-gray-900">{user.name}</span>
              </div>
              
              {showFollowButton && (
                <FollowButton 
                  userId={user._id} 
                  initialStatus={user.followInfo?.status}
                  onFollowChange={(isFollowing, status) => handleFollowChange(user._id, isFollowing, status)}
                />
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  }

  // Render standard list with user cards
  return (
    <div className="space-y-4">
      {updatedUsers.map(user => (
        <UserCard 
          key={user._id} 
          user={user}
          showBio={showBio}
          showStats={showStats}
          showFollowButton={showFollowButton}
          onFollowChange={(isFollowing, status) => handleFollowChange(user._id, isFollowing, status)}
          onUserSelect={onUserSelect}
        />
      ))}
    </div>
  );
};

export default UserList;